import React from 'react';
import { Link } from 'wouter';

// Pure HTML version - minimal React processing
const PatientOnboardingBasic: React.FC = () => {
  React.useEffect(() => {
    // Insert script for Stripe
    const stripeScript = document.createElement('script');
    stripeScript.src = 'https://js.stripe.com/v3/buy-button.js';
    stripeScript.async = true;
    document.head.appendChild(stripeScript);
    
    // Insert script for Typeform
    const typeformScript = document.createElement('script');
    typeformScript.src = 'https://embed.typeform.com/next/embed.js';
    typeformScript.async = true;
    document.head.appendChild(typeformScript);

    // IMPORTANT: Fix for JotForm iframe scrolling with custom script
    const jotformScript = document.createElement('script');
    jotformScript.innerHTML = `
      window.handleIFrameMessage = function(e) {
        if (typeof e.data === 'object') { return; }
        var args = e.data.split(":");
        if (args.length > 2) { iframe = document.getElementById('JotFormIFrame-' + args[(args.length - 1)]); } else { iframe = document.getElementById('JotFormIFrame'); }
        if (!iframe) { return; }
        switch (args[0]) {
          case 'scrollIntoView':
            iframe.scrollIntoView();
            break;
          case 'setHeight':
            iframe.style.height = args[1] + 'px';
            break;
          case 'collapseErrorPage':
            if (iframe.clientHeight > window.innerHeight) {
              iframe.style.height = window.innerHeight + 'px';
            }
            break;
          case 'reloadPage':
            window.location.reload();
            break;
        }
      };
      
      if (window.addEventListener) {
        window.addEventListener('message', handleIFrameMessage, false);
      } else if (window.attachEvent) {
        window.attachEvent('onmessage', handleIFrameMessage);
      }
    `;
    document.head.appendChild(jotformScript);

    return () => {
      // Cleanup scripts when component unmounts
      document.head.removeChild(stripeScript);
      document.head.removeChild(typeformScript);
      document.head.removeChild(jotformScript);
    };
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-white font-sans text-gray-800">
      <header className="bg-indigo-950 text-white py-4 border-b border-indigo-900">
        <div className="container mx-auto px-4 flex justify-between items-center">
          <Link href="/" className="text-xl font-serif font-medium">Next Generation Medicine</Link>
          <Link href="/" className="text-sm hover:text-indigo-200 transition-colors">Home</Link>
        </div>
      </header>

      <div className="bg-indigo-950 text-white py-10">
        <div className="container mx-auto px-4 max-w-5xl">
          <h1 className="text-2xl md:text-3xl font-serif font-medium mb-3">Patient Onboarding</h1>
          <p className="text-base text-indigo-100 max-w-2xl">
            Complete these steps to begin your care journey with Next Generation Medicine.
          </p>
        </div>
      </div>

      <main className="flex-grow">
        {/* Compact Steps Process Indicator */}
        <section className="py-6 bg-gray-50 border-b border-gray-100">
          <div className="container mx-auto px-4 max-w-5xl">
            <div className="flex flex-row items-center justify-center">
              <div className="flex items-center">
                <div className="bg-indigo-800 w-8 h-8 rounded-sm flex items-center justify-center shadow-sm">
                  <p className="text-xs font-bold text-white">1</p>
                </div>
                <span className="ml-2 text-sm font-medium">Payment</span>
              </div>
              
              <div className="w-12 h-[1px] bg-indigo-200 mx-3"></div>
              
              <div className="flex items-center">
                <div className="bg-indigo-800 w-8 h-8 rounded-sm flex items-center justify-center shadow-sm">
                  <p className="text-xs font-bold text-white">2</p>
                </div>
                <span className="ml-2 text-sm font-medium">Consent</span>
              </div>
              
              <div className="w-12 h-[1px] bg-indigo-200 mx-3"></div>
              
              <div className="flex items-center">
                <div className="bg-indigo-200 w-8 h-8 rounded-sm flex items-center justify-center shadow-sm">
                  <p className="text-xs font-bold text-indigo-800">3</p>
                </div>
                <span className="ml-2 text-sm font-medium text-gray-500">Intake</span>
              </div>
            </div>
          </div>
        </section>
      
        {/* Form Sections */}
        <section className="py-8">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="mb-12">
              <h2 className="text-xl font-serif font-medium text-center mb-4">
                <span className="inline-block bg-indigo-800 text-white w-6 h-6 rounded-sm text-xs font-bold mr-2 leading-6">1</span>
                Complete Payment <span className="text-indigo-600">(Required Now)</span>
              </h2>
              
              <div className="border border-indigo-200 bg-white p-6 rounded-sm shadow-lg">
                <div className="max-w-lg mx-auto">
                  <p className="text-gray-600 mb-6 text-center text-sm">
                    To secure your spot as a patient and begin the onboarding process, please complete the payment below.
                  </p>
                  
                  <div 
                    className="flex justify-center items-center"
                    dangerouslySetInnerHTML={{
                      __html: `
                        <script async src="https://js.stripe.com/v3/buy-button.js"></script>
                        <div style="text-align: center; width: 100%; display: flex; justify-content: center;">
                          <stripe-buy-button
                            buy-button-id="buy_btn_1RMbm62MqVjUEqpoTZihJXuR"
                            publishable-key="pk_live_51PYVPf2MqVjUEqpoxikNXOVgHIHI6ZHkB21IvZVeX3eGnHhvlxYxIS2Bp28iWNJBKksoUhjoz1tstoIj005VsKKt00UTFXyAkG"
                          >
                          </stripe-buy-button>
                        </div>
                      `
                    }}
                  />
                  
                  <p className="text-center text-xs text-gray-500 mt-6">
                    Payment is processed securely via Stripe. After completing this payment, please proceed to the consent form.
                  </p>
                </div>
              </div>
            </div>

            <div className="mb-12">
              <h2 className="text-xl font-serif font-medium text-center mb-4">
                <span className="inline-block bg-indigo-800 text-white w-6 h-6 rounded-sm text-xs font-bold mr-2 leading-6">2</span>
                Complete Consent Form <span className="text-indigo-600">(Required Now)</span>
              </h2>
              
              <div className="border border-indigo-200 bg-white p-6 rounded-sm shadow-lg">
                <p className="text-gray-600 mb-6 text-center max-w-lg mx-auto text-sm">
                  Please review and sign our care consent form. This is a required step before we can begin your care.
                </p>
                
                <div dangerouslySetInnerHTML={{
                  __html: `
                    <iframe
                      id="JotFormIFrame-251276365541155"
                      title="Care Consent Form"
                      allowtransparency="true"
                      allowfullscreen="true"
                      allow="geolocation; microphone; camera"
                      src="https://form.jotform.com/251276365541155"
                      frameborder="0"
                      style="min-width:100%;width:100%;height:539px;border:none;"
                      scrolling="yes"
                    >
                    </iframe>
                  `
                }} />
              </div>
            </div>

            <div className="mb-12">
              <h2 className="text-xl font-serif font-medium text-center mb-4">
                <span className="inline-block bg-indigo-200 text-indigo-800 w-6 h-6 rounded-sm text-xs font-bold mr-2 leading-6">3</span>
                Complete Intake Form <span className="text-gray-500">(Can Be Completed Later)</span>
              </h2>
              
              <div className="border border-indigo-200 bg-white p-6 rounded-sm shadow-lg">
                <p className="text-gray-600 mb-6 text-center max-w-lg mx-auto text-sm">
                  This detailed health assessment helps us provide you with personalized care. <strong>You can return to this page later to complete it</strong> if you prefer to take your time with it.
                </p>
                
                <div 
                  style={{ minHeight: "500px" }}
                  dangerouslySetInnerHTML={{
                    __html: `
                      <div data-tf-live="01JTRWC18N64BK4W9G1PZXME8C"></div>
                      <script src="//embed.typeform.com/next/embed.js"></script>
                    `
                  }}
                />
              </div>
            </div>

            {/* What to expect next */}
            <div className="border border-indigo-200 bg-indigo-50 p-6 rounded-sm shadow-lg">
              <div className="max-w-2xl mx-auto">
                <h2 className="text-lg font-serif font-medium text-indigo-950 mb-3">What to Expect Next</h2>
                <p className="text-gray-700 mb-3 text-sm leading-relaxed">
                  <strong>After completing steps 1 and 2</strong>, our team will review your information and reach out within 1-2 business days to schedule your consultation. The intake form (step 3) can be completed at any time before your appointment.
                </p>
                <p className="text-gray-700 text-sm leading-relaxed">
                  Questions? Contact us at <a href="mailto:care@nextgenerationmedicine.com" className="text-indigo-700 hover:text-indigo-900 underline">care@nextgenerationmedicine.com</a>
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>

      <footer className="bg-indigo-950 text-white py-8 mt-auto">
        <div className="container mx-auto px-4 text-center">
          <p className="text-sm text-indigo-200">
            © {new Date().getFullYear()} Next Generation Medicine. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default PatientOnboardingBasic;